// One price list version (M6): each price against the item's price before it — list price,
// discount and net kept apart, the net change in %, and every discount that moved flagged
// (plan §5.4 "implied discount changes flagged"), each change with its direction and rand
// effect (UI6A-7); which version is in effect today (UI6A-8). A version can be withdrawn
// (archived); the one before it then applies again. An older version, already replaced,
// isn't withdrawn (nothing would change): it says which one applies instead; a withdrawn one
// says which applies now (UI6A-111). Packs read as everywhere else: "case of 6 L".

import type { PriceListDetail } from '@enp/domain/api';
import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  Money,
  PageHeader,
  Skeleton,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Archive, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { meQueryOptions } from '../../../../auth/session.js';
import {
  baseQtyText,
  priceListState,
  priceMove,
  priceMoves,
} from '../../../../purchasing/figures.js';
import {
  PriceListStateBadge,
  PurchasingBreadcrumb,
  Section,
  useCan,
} from '../../../../purchasing/parts.js';
import {
  archivePriceList,
  invalidatePurchasing,
  priceListQuery,
  priceListsQuery,
  supplierQuery,
} from '../../../../purchasing/queries.js';
import { discountText } from '../../../../purchasing/words.js';
import { AccountDate } from '../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { Notice } from '../../../../stations/page-parts.js';
import { plainDateLabel, accountToday } from '../../../../stock/labels.js';
import { actionProblem, Fact, Facts } from '../../../../stock-control/parts.js';

export const Route = createFileRoute('/_app/purchasing/price-lists/$priceListId')({
  component: PriceListPage,
});

const CHANGE_LABEL: Record<PriceListDetail['prices'][number]['change'], string> = {
  new: 'New',
  changed: 'Changed',
  unchanged: 'Unchanged',
};

function WithdrawDialog({ pl, onClose }: { pl: PriceListDetail; onClose: () => void }) {
  const queryClient = useQueryClient();
  const run = useMutation({
    mutationFn: () => archivePriceList(pl.id),
    onSuccess: async () => {
      await invalidatePurchasing(queryClient);
      toast.success(`Version ${pl.version_no} withdrawn`, {
        description: 'The version before it applies again from now on.',
      });
      onClose();
    },
  });
  const failed = run.isError ? actionProblem(run.error, 'It wasn’t withdrawn') : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent role="alertdialog" className="max-w-md">
        <DialogForm
          onSubmit={(e) => {
            e.preventDefault();
            run.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Withdraw version {pl.version_no}?</DialogTitle>
            <DialogDescription>
              New requests and orders stop using its prices; the version before it applies again.
              Orders already made keep the prices they were made with. It stays in the history.
            </DialogDescription>
          </DialogHeader>
          {failed && (
            <DialogBody>
              <Notice tone="danger" alert title={failed.title} technical={failed.technical}>
                {failed.explain}
              </Notice>
            </DialogBody>
          )}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" autoFocus variant="destructiveSolid" loading={run.isPending}>
              Withdraw
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function PriceListPage() {
  const { priceListId } = Route.useParams();
  const q = useQuery(priceListQuery(priceListId));
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const canEdit = useCan('purchasing.order', true);
  const [withdrawing, setWithdrawing] = useState(false);
  const pl = q.data;
  const versions = useQuery({
    ...priceListsQuery(pl?.supplier.id ?? ''),
    enabled: Boolean(pl),
  });
  // Pack sizes, so a price reads "per case of 6 L" as in the editor (UI6A-111).
  const supplier = useQuery({ ...supplierQuery(pl?.supplier.id ?? ''), enabled: Boolean(pl) });
  const packSize = new Map(
    (supplier.data?.items ?? []).map((i) => [
      i.id,
      baseQtyText(i.base_qty_per_pack, i.product.base_uom),
    ]),
  );
  useDocumentTitle(pl ? `${pl.supplier.name} price list v${pl.version_no}` : 'Price list');

  if (q.isError && !pl) {
    return (
      <>
        <PurchasingBreadcrumb
          trail={[{ to: '/purchasing/suppliers', label: 'Suppliers' }]}
          page="Price list"
        />
        <div className="mt-4 max-w-2xl">
          <ErrorPanel
            error={q.error}
            titleAs="h1"
            what="This price list"
            onRetry={() => void q.refetch()}
            extraAction={
              <Button variant="secondary" asChild>
                <Link to="/purchasing/suppliers">Back to suppliers</Link>
              </Button>
            }
          />
        </div>
      </>
    );
  }
  if (!pl) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <h1 className="sr-only">Price list</h1>
        <span role="status" className="sr-only">
          Loading the price list…
        </span>
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-8 w-96" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-56 w-full" />
      </div>
    );
  }
  const state = versions.data
    ? priceListState(pl, versions.data.items, today)
    : pl.archived_at
      ? 'withdrawn'
      : pl.effective_from > today
        ? 'scheduled'
        : null;
  const moves = priceMoves(pl.prices);
  // The version that gives today's prices (for an older or withdrawn one).
  const applies = (versions.data?.items ?? []).find(
    (v) => v.id !== pl.id && priceListState(v, versions.data?.items ?? [], today) === 'in_effect',
  );
  const appliesText = applies
    ? `version ${applies.version_no} (from ${plainDateLabel(applies.effective_from, today)})`
    : null;
  const risenByDiscount = pl.prices.filter(
    (p) =>
      p.discount_changed &&
      p.previous &&
      priceMove(p.previous.net_price_cents, p.net_price_cents).direction === 'up',
  ).length;

  return (
    <>
      <PageHeader
        eyebrow={
          <PurchasingBreadcrumb
            trail={[
              { to: '/purchasing/suppliers', label: 'Suppliers' },
              {
                to: '/purchasing/suppliers/$supplierId',
                params: { supplierId: pl.supplier.id },
                label: pl.supplier.name,
              },
            ]}
            page={`Price list v${pl.version_no}`}
          />
        }
        title={
          <span className="flex flex-wrap items-center gap-3">
            {pl.supplier.name}: price list version {pl.version_no}
            {state && (
              <span className="font-body">
                <PriceListStateBadge
                  state={state}
                  effectiveFrom={pl.effective_from}
                  archivedAt={pl.archived_at}
                  today={today}
                />
              </span>
            )}
          </span>
        }
        description={`Takes effect ${plainDateLabel(pl.effective_from, today)}. Prices per pack, ex VAT.`}
        actions={
          canEdit && !pl.archived_at && state !== 'older' ? (
            <Button
              variant="destructive"
              icon={<Archive aria-hidden />}
              onClick={() => setWithdrawing(true)}
            >
              Withdraw…
            </Button>
          ) : undefined
        }
      />
      <div className="flex flex-col gap-5">
        {moves.up > 0 && (
          <Notice
            tone="warning"
            title={`${moves.up} ${moves.up === 1 ? 'price went' : 'prices went'} up against the price before`}
          >
            Check {moves.up === 1 ? 'it' : 'them'} with {pl.supplier.name}; each is marked below
            with how much.
            {risenByDiscount > 0 &&
              ` ${risenByDiscount} of them rose because the discount shrank, not the list price.`}
          </Notice>
        )}
        {moves.up === 0 && pl.discount_changes > 0 && (
          <Notice
            tone="neutral"
            title={`${pl.discount_changes} ${pl.discount_changes === 1 ? 'discount' : 'discounts'} changed; no price went up`}
          >
            {moves.down > 0
              ? `${moves.down} ${moves.down === 1 ? 'price went' : 'prices went'} down. Each change is marked below.`
              : 'Each change is marked below.'}
          </Notice>
        )}
        <Section id="price-list-facts" title="Version">
          <Facts>
            <Fact label="Entered">
              {pl.origin === 'import' ? 'Imported' : `By ${pl.created_by?.name ?? 'someone'}`},{' '}
              <AccountDate value={pl.created_at} withTime />
            </Fact>
            <Fact label="Prices">
              {pl.item_count} {pl.item_count === 1 ? 'item' : 'items'}
            </Fact>
            <Fact label="Note">{pl.note ?? 'None'}</Fact>
            {pl.archived_at && (
              <Fact label="Withdrawn">
                <span data-testid="withdrawn-applies">
                  <AccountDate value={pl.archived_at} withTime />
                  {appliesText ? `; ${appliesText} applies now` : '; no version applies now'}
                </span>
              </Fact>
            )}
            {state === 'older' && (
              <Fact label="In effect instead">
                <span data-testid="older-applies">
                  {appliesText
                    ? `Replaced: ${appliesText} gives today’s prices. Nothing to withdraw.`
                    : 'Replaced by a later version.'}
                </span>
              </Fact>
            )}
          </Facts>
        </Section>
        <Section id="price-list-prices" title="Prices">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table aria-label="Prices in this version" className="w-full border-collapse text-body">
              <thead>
                <tr className="bg-surface-sunken text-left text-xs font-semibold uppercase tracking-wide text-muted">
                  <th scope="col" className="px-3 py-2">
                    Item
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    List
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    Discount
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    Net
                  </th>
                  <th scope="col" className="px-3 py-2">
                    Before
                  </th>
                  <th scope="col" className="px-3 py-2">
                    Change
                  </th>
                </tr>
              </thead>
              <tbody>
                {pl.prices.map((p) => {
                  const move = p.previous
                    ? priceMove(p.previous.net_price_cents, p.net_price_cents)
                    : null;
                  return (
                    <tr
                      key={p.supplier_item.id}
                      className="border-t border-border align-top"
                      data-discount-changed={p.discount_changed || undefined}
                      data-move={move?.direction}
                    >
                      <td className="px-3 py-2">
                        <span className="flex flex-col leading-tight">
                          <span className="font-medium text-fg">
                            {p.supplier_item.product.name}
                          </span>
                          <span className="text-xs text-muted">
                            per {p.supplier_item.pack_uom}
                            {packSize.get(p.supplier_item.id) &&
                              ` of ${packSize.get(p.supplier_item.id)}`}
                            {p.supplier_item.supplier_code && (
                              <>
                                {' '}
                                · their code{' '}
                                <span className="whitespace-nowrap">
                                  {p.supplier_item.supplier_code}
                                </span>
                              </>
                            )}
                          </span>
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Money cents={p.list_price_cents} className="text-fg" />
                      </td>
                      <td className="px-3 py-2 text-right text-fg">
                        {discountText(p.discount_bp)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Money cents={p.net_price_cents} className="font-medium text-fg" />
                      </td>
                      <td className="px-3 py-2 text-sm text-muted">
                        {p.previous ? (
                          <>
                            <Money cents={p.previous.net_price_cents} />
                            {p.previous.discount_bp > 0 && ` (list `}
                            {p.previous.discount_bp > 0 && (
                              <Money cents={p.previous.list_price_cents} />
                            )}
                            {p.previous.discount_bp > 0 &&
                              ` less ${discountText(p.previous.discount_bp)})`}
                          </>
                        ) : (
                          'No earlier price'
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className="flex flex-col items-start gap-1">
                          <span className="text-sm text-fg">
                            {move && move.direction !== 'same' ? (
                              <span
                                className={cn(
                                  'whitespace-nowrap font-medium',
                                  move.direction === 'up'
                                    ? 'text-state-warning'
                                    : 'text-state-success',
                                )}
                                data-testid="price-move"
                              >
                                {move.direction === 'up' ? 'Up ' : 'Down '}
                                {move.text}
                              </span>
                            ) : (
                              CHANGE_LABEL[p.change]
                            )}
                          </span>
                          {p.discount_changed && p.previous && (
                            <Badge tone={move?.direction === 'up' ? 'warning' : 'neutral'}>
                              {move?.direction === 'up' && <TriangleAlert aria-hidden />}
                              Discount {discountText(p.previous.discount_bp).toLowerCase()} →{' '}
                              {discountText(p.discount_bp).toLowerCase()}
                            </Badge>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-muted">
            <Link
              to="/purchasing/suppliers/$supplierId"
              params={{ supplierId: pl.supplier.id }}
              className="rounded-sm font-medium text-primary-text underline-offset-2 hover:underline"
            >
              Every version of {pl.supplier.name}’s prices
            </Link>
          </p>
        </Section>
      </div>
      {withdrawing && <WithdrawDialog pl={pl} onClose={() => setWithdrawing(false)} />}
    </>
  );
}
